import { Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { comIdempotencia, novaChaveIdempotencia } from 'src/interceptors/idempotencia-interceptor';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { TabContainer, TabContent } from '@/shared/components/layout/tab-container/tab-container';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { PrestadorAnotacao, PrestadorAnotacaoTipo } from '@/core/api/sicom-api/registro/prestador/prestador-anotacoes.model';
import { PrestadorAnotacoesService } from '@/core/api/sicom-api/registro/prestador/prestador-anotacoes.service';
import { DateUtils } from '@/shared/utils/date-utils';
import { NovaAnotacaoDialog } from '@/shared/components/anotacoes/dialogs/nova-anotacao/nova-anotacao';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { AppMenuItem, PermissionInput } from '@/core/permissions/permission.model';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    selector: 'app-anotacoes-prestador',
    standalone: true,
    imports: [DialogoPadraoDirective, BotaoAcoes, Button, Dialog, CardContent, CustomTableComponent, TabContainer, TabContent, NovaAnotacaoDialog],
    templateUrl: './anotacoes-prestador.html',
    styleUrl: './anotacoes-prestador.scss'
})
export class AnotacoesPrestador {
    pessoa = input<Pessoa>();
    /** Role da ação de escrita (criação/exclusão de anotação). Sem valor → sem gate de role. */
    readonly roleCriar = input<PermissionInput | undefined>(undefined);
    readonly roleExcluir = input<PermissionInput | undefined>(undefined);
    private readonly service = inject(PrestadorAnotacoesService);
    private readonly notificacoes = inject(NotificacoesService);
    private readonly destroyRef = inject(DestroyRef);
    readonly linhas = signal<PrestadorAnotacao[]>([]);
    readonly linhasTabela = computed(() => this.linhas().map((item) => ({ ...item, data: DateUtils.formatDate(item.data) })));
    readonly loading = signal(false);
    readonly anotacaoVisualizacao = signal<{ data?: string | null; historico?: string | null } | null>(null);
    readonly modalVisualizacaoAnotacao = signal(false);
    readonly modalNovaAnotacao = signal(false);
    /** Gravação da nova anotação em curso — trava o botão do diálogo contra duplo envio. */
    readonly salvandoAnotacao = signal(false);
    /**
     * Chave de idempotência da sessão do diálogo: nasce ao abrir e vale para todas as tentativas
     * dali. Reabrir gera outra, que é como o usuário registra duas anotações iguais de propósito.
     */
    private chaveIdempotencia = novaChaveIdempotencia();
    protected readonly EInconPos = EInconPos;

    get itensAcoes(): AppMenuItem[] {
        return [
            acaoMenuItem({ id: 'adicionar', label: 'Adicionar', icon: 'pi pi-plus', cor: 'novo', disabled: !this.permiteAdicionar, permission: this.roleCriar(), command: () => this.abrirNovaAnotacao() }),
            acaoMenuItem({ id: 'atualizar', label: 'Atualizar', icon: 'pi pi-refresh', cor: 'info', disabled: this.loading(), command: () => this.carregar() })
        ];
    }

    selected: PrestadorAnotacaoTipo = 'CONTRATO_SOCIAL';
    readonly options = [
        { label: 'Contrato Social', value: 'CONTRATO_SOCIAL', icon: 'pi pi-file-edit' },
        { label: 'Dívida Ativa', value: 'DIVIDA_ATIVA', icon: 'pi pi-exclamation-triangle' },
        { label: 'Lançamentos', value: 'LANCAMENTOS', icon: 'pi pi-dollar' },
        { label: 'Portal de Serviços', value: 'PORTAL_SERVICOS', icon: 'pi pi-globe' },
        { label: 'Gerais', value: 'GERAIS', icon: 'pi pi-list' }
    ];
    readonly columnOrder = ['data', 'historico'];
    readonly metadata: TableMetadata = {
        headers: { data: { type: 'text', value: 'Data', default: '-', hidden: false }, historico: { type: 'text', value: 'Histórico', default: '-', hidden: false } },
        title: 'historico',
        actions: {
            visualizar: { label: 'Visualizar', icon: 'pi pi-eye', color: 'info', command: (item) => this.visualizarAnotacao(item) }
        },
        defaultSortField: 'data',
        defaultSortOrder: -1
    };
    constructor() {
        effect(() => {
            if (this.pessoa()?.id && this.pessoa()?.uf) this.carregar();
        });
    }
    carregar(): void {
        const pessoa = this.pessoa();
        if (!pessoa) return;
        this.loading.set(true);
        this.requestPorAba(pessoa.id, pessoa.uf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (resposta) => {
                    this.linhas.set(resposta.content);
                    this.loading.set(false);
                },
                // Sem limpar, a falha na troca de prestador deixaria as anotacoes do anterior na tela.
                error: () => {
                    this.linhas.set([]);
                    this.loading.set(false);
                }
            });
    }
    alterarAba(valor: string): void {
        this.selected = valor as PrestadorAnotacaoTipo;
        this.carregar();
    }

    visualizarAnotacao(item: { data?: string | null; historico?: string | null }): void {
        this.anotacaoVisualizacao.set(item);
        this.modalVisualizacaoAnotacao.set(true);
    }

    fecharVisualizacaoAnotacao(): void {
        this.modalVisualizacaoAnotacao.set(false);
        this.anotacaoVisualizacao.set(null);
    }

    // Adicionar anotações é permitido em todas as abas, exceto Portal de Serviços (regra do legado).
    get permiteAdicionar(): boolean {
        return this.selected !== 'PORTAL_SERVICOS';
    }

    abrirNovaAnotacao(): void {
        this.chaveIdempotencia = novaChaveIdempotencia();
        this.modalNovaAnotacao.set(true);
    }

    onAnotacaoCriada(event: { dtHistoricoMedico: string; txHistoricoMedico: string }): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf) {
            return;
        }
        this.salvandoAnotacao.set(true);
        this.service
            .criar({ id: pessoa.id, uf: pessoa.uf, tipo: this.selected, data: event.dtHistoricoMedico, historico: event.txHistoricoMedico }, comIdempotencia(this.chaveIdempotencia))
            .pipe(
                finalize(() => this.salvandoAnotacao.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.modalNovaAnotacao.set(false);
                    this.notificacoes.notificar({ severity: 'success', summary: 'Anotação adicionada.' });
                    this.carregar();
                },
                // Sem toast aqui: o interceptor global já exibiu a mensagem que o backend mandou, e
                // ela é mais específica que qualquer texto fixo. O diálogo fica aberto com o texto
                // digitado — só o sucesso o fecha.
                error: () => {}
            });
    }

    private requestPorAba(id: number, uf: string) {
        switch (this.selected) {
            case 'CONTRATO_SOCIAL':
                return this.service.listarContratoSocial(id, uf);
            case 'DIVIDA_ATIVA':
                return this.service.listarDividaAtiva(id, uf);
            case 'LANCAMENTOS':
                return this.service.listarLancamentos(id, uf);
            case 'PORTAL_SERVICOS':
                return this.service.listarPortalServicos(id, uf);
            case 'GERAIS':
                return this.service.listarGerais(id, uf);
        }
    }
}
